import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTeamStore } from '../../lib/store/teamStore';
import Card from '../common/Card';
import { ShiftTypeIcon } from './ShiftTypeIcon';

type ShiftType = 'days_only' | 'nights_only' | 'both';

export const ShiftTypeDistributionChart = () => {
  const { teamId } = useParams<{ teamId: string }>();
  const { teamMembers, fetchTeamMembers } = useTeamStore();
  const [distribution, setDistribution] = useState<Record<ShiftType, number>>({
    'days_only': 0,
    'nights_only': 0,
    'both': 0
  });
  
  useEffect(() => {
    if (teamId) {
      fetchTeamMembers(teamId);
    }
  }, [teamId, fetchTeamMembers]);
  
  useEffect(() => {
    if (teamMembers.length > 0) {
      const counts = teamMembers.reduce((acc, member) => {
        acc[member.shift_type]++;
        return acc;
      }, {
        'days_only': 0,
        'nights_only': 0,
        'both': 0
      } as Record<ShiftType, number>);
      
      setDistribution(counts);
    }
  }, [teamMembers]);
  
  const total = teamMembers.length;
  
  return (
    <Card className="p-6">
      <h2 className="text-xl font-semibold mb-4">Shift Type Distribution</h2>
      
      <div className="grid grid-cols-3 gap-4">
        <div className="text-center">
          <div className="flex justify-center">
            <ShiftTypeIcon type="days_only" className="h-8 w-8" />
          </div>
          <div className="mt-2 text-2xl font-bold">{distribution['days_only']}</div>
          <div className="text-sm text-gray-500">Days Only</div>
          <div className="text-xs text-gray-400">
            {total > 0 ? Math.round((distribution['days_only'] / total) * 100) : 0}%
          </div>
        </div>
        
        <div className="text-center">
          <div className="flex justify-center">
            <ShiftTypeIcon type="nights_only" className="h-8 w-8" />
          </div>
          <div className="mt-2 text-2xl font-bold">{distribution['nights_only']}</div>
          <div className="text-sm text-gray-500">Nights Only</div>
          <div className="text-xs text-gray-400">
            {total > 0 ? Math.round((distribution['nights_only'] / total) * 100) : 0}%
          </div>
        </div>
        
        <div className="text-center">
          <div className="flex justify-center">
            <ShiftTypeIcon type="both" className="h-8 w-8" />
          </div>
          <div className="mt-2 text-2xl font-bold">{distribution['both']}</div>
          <div className="text-sm text-gray-500">Both Shifts</div>
          <div className="text-xs text-gray-400">
            {total > 0 ? Math.round((distribution['both'] / total) * 100) : 0}%
          </div>
        </div>
      </div>
      
      {total > 0 && (
        <div className="mt-4 bg-gray-200 rounded-full h-2.5 overflow-hidden">
          <div className="flex h-full">
            <div
              className="bg-yellow-500"
              style={{ width: `${(distribution['days_only'] / total) * 100}%` }}
            ></div>
            <div
              className="bg-indigo-500"
              style={{ width: `${(distribution['nights_only'] / total) * 100}%` }}
            ></div>
            <div
              className="bg-purple-500"
              style={{ width: `${(distribution['both'] / total) * 100}%` }}
            ></div>
          </div>
        </div>
      )}
    </Card>
  );
}; 